import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import {
  useAttractionsQuery,
  useRemoveAttractionMutation,
  useSaveAttractionMutation,
} from '@/queries/attractions';
import { withServerDateFields } from '@/lib/server-date';
import Portal from '../../modal/Portal';
import { SectionNavbar } from '../../section-navbar';
import { AssignedSponsors } from '../../sponsors';
import { AttractionForm } from '../../attractions';
import {
  EventRelatedItemRow,
  EventRelatedSection,
} from '../EventRelatedSection';

export function AttractionsSection({ festivalId }) {
  const attractionsQ = useAttractionsQuery(festivalId);
  const saveMutation = useSaveAttractionMutation(festivalId);
  const removeMutation = useRemoveAttractionMutation(festivalId);

  const attractions = attractionsQ.data ?? [];
  const loading =
    festivalId == null || attractionsQ.isPending || attractionsQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const openModal = (attraction) => {
    const next =
      attraction?.attractionId != null
        ? attractions.find((a) => a.attractionId === attraction.attractionId) ||
          {}
        : {};
    setSelectedAttraction(next);
    setShowModal(true);
  };

  const handleDateChange = (dates) => {
    setSelectedAttraction((prev) => ({ ...prev, ...dates[0] }));
  };

  const handleLocationChange = (location) => {
    setSelectedAttraction((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        ...location,
        radius: 50,
      },
    }));
  };

  const handleLocationNameChange = (locationName) => {
    setSelectedAttraction((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        locationName,
      },
    }));
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedAttraction((prev) => ({
      ...prev,
      photoURL: url,
      attractionPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedAttraction((prev) => ({
      ...prev,
      photoURL: null,
      attractionPhoto: null,
    }));
  };

  const handleSave = async (attraction) => {
    const updatedAttraction = withServerDateFields({
      ...selectedAttraction,
      ...attraction,
    });
    try {
      await saveMutation.mutateAsync(updatedAttraction);
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const requestDelete = useCallback((attraction) => {
    if (!attraction?.attractionId) {
      return;
    }
    setPendingDelete(attraction);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const attraction = pendingDelete;
    if (!attraction?.attractionId) {
      return;
    }
    try {
      await removeMutation.mutateAsync(attraction.attractionId);
      setPendingDelete(null);
      if (selectedAttraction.attractionId === attraction.attractionId) {
        setShowModal(false);
        setSelectedAttraction({});
      }
    } catch {
      /* mutation toast */
    }
  }, [pendingDelete, removeMutation, selectedAttraction.attractionId]);

  const attractionForm = (
    <AttractionForm
      attraction={selectedAttraction}
      handleSave={handleSave}
      onDateChange={handleDateChange}
      onLocationChange={handleLocationChange}
      onLocationNameChange={handleLocationNameChange}
      onFileUpload={onFileUpload}
      onFileDelete={onFileDelete}
    />
  );

  const sections = [
    { title: 'Information', component: attractionForm },
    {
      title: 'Sponsors',
      component: (
        <AssignedSponsors
          objectId={selectedAttraction.attractionId}
          objectType="attraction"
        />
      ),
    },
  ];

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'star']}
        title="Attractions"
        loading={loading}
        onAdd={() => openModal({})}
        addLabel="Add Attraction"
      >
        {attractions.map((attraction) => (
          <EventRelatedItemRow
            key={attraction.attractionId}
            label={attraction.attractionName || 'Untitled attraction'}
            onEdit={() => openModal(attraction)}
            onDelete={() => requestDelete(attraction)}
          />
        ))}
      </EventRelatedSection>

      <Portal
        header={selectedAttraction?.attractionName || 'Add New Attraction'}
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <SectionNavbar navItems={sections} />
      </Portal>

      <Portal
        header="Delete attraction"
        open={Boolean(pendingDelete)}
        onClose={cancelDelete}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to delete this attraction?
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={removeMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
